import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText, Building2, Landmark, ChevronDown, Loader2,
    RotateCcw, Send, CheckCircle, Search, X,
    Hash, IndianRupee, StickyNote,
    ShieldCheck,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchLCVendorCodes,
    fetchPOForVendor,
    fetchBGClientCodes,
    fetchSubClientsForBGClient,
    fetchClientPOsForBG,
    fetchLCBGFDNumbers,
    submitLCBGData,
    clearPOList,
    clearSubClients,
    clearBGPoList,
    clearSaveResult,
    resetLCBGCreation,
    selectLCVendors,
    selectLCVendorsLoading,
    selectPOList,
    selectPOLoading,
    selectBGClients,
    selectBGClientsLoading,
    selectSubClients,
    selectSubClientsLoading,
    selectBGPoList,
    selectBGPoLoading,
    selectFDList,
    selectFDLoading,
    selectLCBGSaveStatus,
    selectLCBGSaveLoading,
    selectLCBGSaveError,
} from '../../slices/accountsSlice/lcbgCreationSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const LCBG_TYPES = [
    { value: 'LC', label: 'Letter of Credit', icon: FileText,    desc: 'Vendor-based' },
    { value: 'BG', label: 'Bank Guarantee',   icon: ShieldCheck, desc: 'Client-based' },
];

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI (compact) ───────────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

// Sub-section heading inside the single form card
const SubHeading = ({ icon: Icon, children }) => (
    <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
        <Icon className="h-3.5 w-3.5 text-indigo-500" /> {children}
    </p>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

// ── Multi-Select PO Dropdown ──────────────────────────────────────────────────
// valueKey / labelKey allow reuse for both LC (Vendorpos/VendorpoName) and BG (Clientpos/ClientpoName)

const POMultiSelect = ({ poList, loading, selected, onChange, disabled, valueKey = 'Vendorpos', labelKey = 'VendorpoName' }) => {
    const [open, setOpen]     = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = poList.filter(p =>
        p[labelKey]?.toLowerCase().includes(search.toLowerCase())
    );

    const toggleItem = (val) => {
        if (selected.includes(val)) onChange(selected.filter(v => v !== val));
        else onChange([...selected, val]);
    };

    const toggleAll = () => {
        if (selected.length === filtered.length) onChange([]);
        else onChange(filtered.map(p => p[valueKey]));
    };

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                disabled={disabled || loading}
                onClick={() => setOpen(o => !o)}
                className={`${inputCls} text-left flex items-center justify-between`}
            >
                <span className={selected.length === 0 ? 'text-gray-400 dark:text-gray-500' : ''}>
                    {loading
                        ? 'Loading POs…'
                        : selected.length === 0
                            ? 'Select PO Numbers'
                            : `${selected.length} PO${selected.length > 1 ? 's' : ''} selected`}
                </span>
                {loading
                    ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin flex-shrink-0" />
                    : <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />}
            </button>

            {open && !loading && (
                <div className="absolute z-[9999] mt-1.5 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search PO…"
                                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:border-indigo-400"
                            />
                        </div>
                    </div>

                    {filtered.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="w-full px-3 py-2 text-left text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-100 dark:border-gray-700"
                        >
                            {selected.length === filtered.length ? 'Deselect All' : 'Select All'} ({filtered.length})
                        </button>
                    )}

                    <div className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <p className="px-3 py-4 text-xs text-gray-400 text-center">No POs found</p>
                        ) : filtered.map((p, i) => (
                            <label
                                key={`${p[valueKey]}_${i}`}
                                className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(p[valueKey])}
                                    onChange={() => toggleItem(p[valueKey])}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{p[labelKey]}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {selected.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {selected.map(v => (
                        <span
                            key={v}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium border border-indigo-100 dark:border-indigo-800"
                        >
                            {v}
                            <button type="button" onClick={() => toggleItem(v)}>
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────

const LCBGCreation = ({ menuData }) => {
    const dispatch     = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────────
    const vendors           = useSelector(selectLCVendors);
    const vendorsLoading    = useSelector(selectLCVendorsLoading);
    const poList            = useSelector(selectPOList);
    const poLoading         = useSelector(selectPOLoading);
    const bgClients         = useSelector(selectBGClients);
    const bgClientsLoading  = useSelector(selectBGClientsLoading);
    const subClients        = useSelector(selectSubClients);
    const subClientsLoading = useSelector(selectSubClientsLoading);
    const bgPoList          = useSelector(selectBGPoList);
    const bgPoLoading       = useSelector(selectBGPoLoading);
    const fdList            = useSelector(selectFDList);
    const fdLoading         = useSelector(selectFDLoading);
    const saveStatus        = useSelector(selectLCBGSaveStatus);
    const saveLoading       = useSelector(selectLCBGSaveLoading);
    const saveError         = useSelector(selectLCBGSaveError);

    // ── Party & PO ──────────────────────────────────────────────────────────────
    const [lcbgType,      setLcbgType]      = useState('LC');
    const [vendorCode,    setVendorCode]    = useState('');
    const [clientCode,    setClientCode]    = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [selectedPOs,   setSelectedPOs]   = useState([]);
    const [fdNo,          setFdNo]          = useState('');

    // ── Details ─────────────────────────────────────────────────────────────────
    const [lcbgNo,       setLcbgNo]       = useState('');
    const [openingDate,  setOpeningDate]   = useState(null);
    const [validityDate, setValidityDate]  = useState(null);
    const [tolerance,    setTolerance]     = useState('');
    const [basicValue,   setBasicValue]    = useState('');
    const [gstValue,     setGstValue]      = useState('');
    const [charges,      setCharges]       = useState('');
    const [lcbgRemarks,  setLcbgRemarks]   = useState('');
    const [bankRemarks,  setBankRemarks]   = useState('');

    const totalValue = (
        (parseFloat(basicValue) || 0) +
        (parseFloat(gstValue)   || 0) +
        (parseFloat(charges)    || 0)
    ).toFixed(2);

    // ── Init ───────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchLCVendorCodes({ RoleId: roleId, Userid: userId }));
        dispatch(fetchBGClientCodes({ RoleId: roleId, Userid: userId }));
        dispatch(fetchLCBGFDNumbers());
        return () => dispatch(resetLCBGCreation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Reset party + POs when type changes
    useEffect(() => {
        setVendorCode(''); setClientCode(''); setSubClientCode('');
        setSelectedPOs([]);
        dispatch(clearPOList());
        dispatch(clearSubClients());
        dispatch(clearBGPoList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lcbgType]);

    // LC — fetch POs when vendor changes
    useEffect(() => {
        if (lcbgType !== 'LC') return;
        if (!vendorCode) { dispatch(clearPOList()); setSelectedPOs([]); return; }
        dispatch(fetchPOForVendor({ VendorCode: vendorCode }));
        setSelectedPOs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorCode]);

    // BG — fetch sub-clients when client changes
    useEffect(() => {
        if (lcbgType !== 'BG') return;
        setSubClientCode('');
        setSelectedPOs([]);
        dispatch(clearSubClients());
        dispatch(clearBGPoList());
        if (!clientCode) return;
        dispatch(fetchSubClientsForBGClient({ ClientCode: clientCode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientCode]);

    // BG — fetch client POs when sub-client changes
    useEffect(() => {
        if (lcbgType !== 'BG') return;
        setSelectedPOs([]);
        dispatch(clearBGPoList());
        if (!clientCode || !subClientCode) return;
        dispatch(fetchClientPOsForBG({ ClientCode: clientCode, Subclient: subClientCode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subClientCode]);

    // Handle save result
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('LC/BG data submitted successfully!');
            handleReset();
        } else if (saveStatus === 'failed') {
            toast.error(saveError || 'Submission failed. Please try again.');
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Validation ─────────────────────────────────────────────────────────────
    const validateStep1 = useCallback(() => {
        if (lcbgType === 'LC' && !vendorCode)   { toast.warn('Please select a vendor.');         return false; }
        if (lcbgType === 'BG' && !clientCode)    { toast.warn('Please select a client.');         return false; }
        if (lcbgType === 'BG' && !subClientCode) { toast.warn('Please select a sub-client.');     return false; }
        if (selectedPOs.length === 0)             { toast.warn('Please select at least one PO.');  return false; }
        return true;
    }, [lcbgType, vendorCode, clientCode, subClientCode, selectedPOs]);

    const validateStep2 = useCallback(() => {
        if (!lcbgNo.trim())  { toast.warn('Please enter the LC/BG number.');   return false; }
        if (!openingDate)     { toast.warn('Please select the opening date.');  return false; }
        if (!validityDate)    { toast.warn('Please select the validity date.'); return false; }
        if (!basicValue || parseFloat(basicValue) <= 0) {
            toast.warn('Please enter a valid basic value.'); return false;
        }
        return true;
    }, [lcbgNo, openingDate, validityDate, basicValue]);

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        // Single-page form — run both former step validations before submitting
        if (!validateStep1() || !validateStep2()) return;
        dispatch(submitLCBGData({
            LCBGType:      lcbgType,
            VendorCode:    lcbgType === 'LC' ? vendorCode    : '',
            PONO:          selectedPOs.map((v) => `${v},`).join(''),
            ClientCode:    lcbgType === 'BG' ? clientCode    : '',
            SubClientCode: lcbgType === 'BG' ? subClientCode : '',
            LCBGno:        lcbgNo,
            OpeningDate:   formatDateForAPI(openingDate),
            ValidityDate:  formatDateForAPI(validityDate),
            Tolerance:     String(tolerance || '0'),
            FD:            fdNo || '',
            BasicValue:    String(basicValue || '0'),
            GSTValue:      String(gstValue   || '0'),
            Charges:       String(charges    || '0'),
            TotalValue:    String(totalValue),
            LCBGRemarks:   lcbgRemarks || '',
            BankRemarks:   bankRemarks || '',
            Polength:      parseInt(selectedPOs.length, 10),
            CreatedBy:     userName,
            RoleId:        parseInt(roleId, 10),
        }));
    };

    // ── Reset ──────────────────────────────────────────────────────────────────
    const handleReset = () => {
        setLcbgType('LC');
        setVendorCode(''); setClientCode(''); setSubClientCode('');
        setSelectedPOs([]); setFdNo('');
        setLcbgNo(''); setOpeningDate(null); setValidityDate(null);
        setTolerance(''); setBasicValue(''); setGstValue(''); setCharges('');
        setLcbgRemarks(''); setBankRemarks('');
        dispatch(resetLCBGCreation());
        dispatch(fetchLCVendorCodes({ RoleId: roleId, Userid: userId }));
        dispatch(fetchBGClientCodes({ RoleId: roleId, Userid: userId }));
        dispatch(fetchLCBGFDNumbers());
    };

    const selectedVendor    = vendors.find(v => v.VendorCode === vendorCode);
    const selectedClient    = bgClients.find(c => c.ClientCode === clientCode);
    const selectedSubClient = subClients.find(s => s.SubClientCode === subClientCode);

    const money = (v) => `₹ ${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    const partyLabel = lcbgType === 'LC'
        ? (selectedVendor?.VendorName || vendorCode)
        : [selectedClient?.ClientName || clientCode, selectedSubClient?.SubClientName || subClientCode].filter(Boolean).join(' / ');
    const fdLabel = fdList.find(fd => String(fd.Fdid) === String(fdNo))?.FdVal || '';

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Landmark className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'LC / BG Creation'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Purchase Module · Letter of Credit &amp; Bank Guarantee</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Landmark} title="LC / BG Details" />
                    <div className="p-4 space-y-4">

                        {/* Party & PO */}
                        <div>
                            <SubHeading icon={Building2}>Instrument, Party &amp; PO</SubHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                                <div>
                                    <Label required>Type</Label>
                                    <div className="flex gap-2">
                                        {LCBG_TYPES.map(t => {
                                            const Icon   = t.icon;
                                            const active = lcbgType === t.value;
                                            return (
                                                <button
                                                    key={t.value}
                                                    type="button"
                                                    onClick={() => setLcbgType(t.value)}
                                                    title={`${t.label} — ${t.desc}`}
                                                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border text-sm font-semibold transition-all
                                                        ${active
                                                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-400'}`}
                                                >
                                                    <Icon className="h-4 w-4" /> {t.value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {lcbgType === 'LC' ? (
                                    <div className="lg:col-span-2">
                                        <Label required>Vendor</Label>
                                        <div className="relative">
                                            <select
                                                value={vendorCode}
                                                onChange={e => setVendorCode(e.target.value)}
                                                className={`${inputCls} appearance-none pr-8`}
                                                disabled={vendorsLoading}
                                            >
                                                <option value="">{vendorsLoading ? 'Loading vendors…' : '— Select Vendor —'}</option>
                                                {vendors.map((v, i) => (
                                                    <option key={`${v.VendorCode}_${i}`} value={v.VendorCode}>{v.VendorName}</option>
                                                ))}
                                            </select>
                                            <SelectIcon loading={vendorsLoading} />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <Label required>Client</Label>
                                            <div className="relative">
                                                <select
                                                    value={clientCode}
                                                    onChange={e => setClientCode(e.target.value)}
                                                    className={`${inputCls} appearance-none pr-8`}
                                                    disabled={bgClientsLoading}
                                                >
                                                    <option value="">{bgClientsLoading ? 'Loading clients…' : '— Select Client —'}</option>
                                                    {bgClients.map((c, i) => (
                                                        <option key={`cl_${c.ClientCode}_${i}`} value={c.ClientCode}>{c.ClientName}</option>
                                                    ))}
                                                </select>
                                                <SelectIcon loading={bgClientsLoading} />
                                            </div>
                                        </div>
                                        <div>
                                            <Label required>Sub-Client</Label>
                                            <div className="relative">
                                                <select
                                                    value={subClientCode}
                                                    onChange={e => setSubClientCode(e.target.value)}
                                                    className={`${inputCls} appearance-none pr-8`}
                                                    disabled={!clientCode || subClientsLoading}
                                                >
                                                    <option value="">
                                                        {!clientCode ? 'Select client first' : subClientsLoading ? 'Loading sub-clients…' : '— Select Sub-Client —'}
                                                    </option>
                                                    {subClients.map((s, i) => (
                                                        <option key={`scl_${s.SubClientCode}_${i}`} value={s.SubClientCode}>{s.SubClientName}</option>
                                                    ))}
                                                </select>
                                                <SelectIcon loading={subClientsLoading} />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* PO multi-select */}
                                <div className="sm:col-span-2">
                                    <Label required>PO Numbers</Label>
                                    {lcbgType === 'LC' ? (
                                        <>
                                            <POMultiSelect
                                                poList={poList}
                                                loading={poLoading}
                                                selected={selectedPOs}
                                                onChange={setSelectedPOs}
                                                disabled={!vendorCode}
                                                valueKey="Vendorpos"
                                                labelKey="VendorpoName"
                                            />
                                            {!vendorCode && <p className="mt-1 text-[11px] text-amber-500">Select a vendor first to load PO numbers.</p>}
                                            {vendorCode && !poLoading && poList.length === 0 && <p className="mt-1 text-[11px] text-gray-400">No PO numbers found for this vendor.</p>}
                                        </>
                                    ) : (
                                        <>
                                            <POMultiSelect
                                                poList={bgPoList}
                                                loading={bgPoLoading}
                                                selected={selectedPOs}
                                                onChange={setSelectedPOs}
                                                disabled={!clientCode || !subClientCode}
                                                valueKey="Clientpos"
                                                labelKey="ClientpoName"
                                            />
                                            {(!clientCode || !subClientCode) && (
                                                <p className="mt-1 text-[11px] text-amber-500">
                                                    {!clientCode ? 'Select a client first.' : 'Select a sub-client to load PO numbers.'}
                                                </p>
                                            )}
                                            {clientCode && subClientCode && !bgPoLoading && bgPoList.length === 0 && <p className="mt-1 text-[11px] text-gray-400">No PO numbers found.</p>}
                                        </>
                                    )}
                                </div>

                                {/* FD Number */}
                                <div>
                                    <Label>FD Number <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                                    <div className="relative">
                                        <select
                                            value={fdNo}
                                            onChange={e => setFdNo(e.target.value)}
                                            className={`${inputCls} appearance-none pr-8`}
                                            disabled={fdLoading}
                                        >
                                            <option value="">{fdLoading ? 'Loading…' : '— None —'}</option>
                                            {fdList.map(fd => (
                                                <option key={fd.Fdid} value={fd.Fdid}>{fd.FdVal}</option>
                                            ))}
                                        </select>
                                        <SelectIcon loading={fdLoading} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reference & Dates */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                            <SubHeading icon={Hash}>Reference &amp; Dates</SubHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <div>
                                    <Label required>{lcbgType} Number</Label>
                                    <input
                                        type="text"
                                        value={lcbgNo}
                                        onChange={e => setLcbgNo(e.target.value)}
                                        placeholder={`Enter ${lcbgType} number`}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <Label required>Opening Date</Label>
                                    <CustomDatePicker
                                        value={openingDate}
                                        onChange={setOpeningDate}
                                        format="DD-MMM-YYYY"
                                        placeholder="Opening date"
                                    />
                                </div>
                                <div>
                                    <Label required>Validity Date</Label>
                                    <CustomDatePicker
                                        value={validityDate}
                                        onChange={setValidityDate}
                                        format="DD-MMM-YYYY"
                                        placeholder="Validity date"
                                        minDate={openingDate}
                                    />
                                </div>
                                <div>
                                    <Label>Tolerance (%)</Label>
                                    <input
                                        type="number"
                                        value={tolerance}
                                        onChange={e => setTolerance(e.target.value)}
                                        placeholder="0.00"
                                        min="0" max="100" step="0.01"
                                        className={inputCls}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Financial Values */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                            <SubHeading icon={IndianRupee}>Financial Values</SubHeading>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <div>
                                    <Label required>Basic Value</Label>
                                    <input type="number" value={basicValue} onChange={e => setBasicValue(e.target.value)}
                                        placeholder="0.00" min="0" step="0.01" className={inputCls} />
                                </div>
                                <div>
                                    <Label>GST Value</Label>
                                    <input type="number" value={gstValue} onChange={e => setGstValue(e.target.value)}
                                        placeholder="0.00" min="0" step="0.01" className={inputCls} />
                                </div>
                                <div>
                                    <Label>Charges</Label>
                                    <input type="number" value={charges} onChange={e => setCharges(e.target.value)}
                                        placeholder="0.00" min="0" step="0.01" className={inputCls} />
                                </div>
                                <div>
                                    <Label>Total Value</Label>
                                    <div className={`${inputCls} bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 font-semibold cursor-default`}>
                                        {money(totalValue)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                            <SubHeading icon={StickyNote}>Remarks</SubHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{lcbgType} Remarks</Label>
                                    <textarea value={lcbgRemarks} onChange={e => setLcbgRemarks(e.target.value)} rows={2}
                                        placeholder={`Enter ${lcbgType} remarks…`} className={`${inputCls} resize-y`} />
                                </div>
                                <div>
                                    <Label>Bank Remarks</Label>
                                    <textarea value={bankRemarks} onChange={e => setBankRemarks(e.target.value)} rows={2}
                                        placeholder="Enter bank remarks…" className={`${inputCls} resize-y`} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Type"     value={lcbgType === 'LC' ? 'Letter of Credit' : 'Bank Guarantee'} />
                                <SumRow label={lcbgType === 'LC' ? 'Vendor' : 'Client'} value={partyLabel} />
                                <SumRow label="POs"      value={selectedPOs.length ? `${selectedPOs.length} selected` : ''} />
                                <SumRow label="FD"       value={fdLabel || 'Not selected'} />
                                <SumRow label={`${lcbgType} No.`} value={lcbgNo} />
                                <SumRow label="Opening"  value={formatDateForAPI(openingDate)} />
                                <SumRow label="Validity" value={formatDateForAPI(validityDate)} />
                                <SumRow label="Tolerance" value={tolerance ? `${tolerance}%` : ''} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="Basic"   value={money(basicValue)} />
                                <SumRow label="GST"     value={money(gstValue)} />
                                <SumRow label="Charges" value={money(charges)} />
                            </dl>

                            <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Total {lcbgType} Value</p>
                                <p className="text-2xl font-black">{money(totalValue)}</p>
                            </div>

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={saveLoading}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={saveLoading}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-sm font-semibold shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                                        : <><Send className="h-4 w-4" /> Submit</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LCBGCreation;
